import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, LogOut, Shield, Menu, X, Tv, Crown, Baby, Download, UserPlus } from "lucide-react";
import { InviteDialog } from "@/components/InviteDialog";
import { InboxBell } from "@/components/InboxBell";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { SettingsMenu } from "@/components/SettingsMenu";

const NAV = [
  { to: "/", key: "home" },
  { to: "/live", key: "live" },
  { to: "/movies", key: "movies" },
  { to: "/guide", key: "guide" },
  { to: "/games", key: "games" },
  { to: "/radio", key: "radio" },
  { to: "/my-list", key: "mylist" },
];

export const Navbar = () => {
  const { user, logout, kids, setKids, paid, activeProfile } = useAuth();
  const { freeAccess } = useCurrency();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) { setOpen(false); nav(`/search?q=${encodeURIComponent(q.trim())}`); }
  };

  return (
    <nav data-testid="main-navbar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="px-4 sm:px-8 lg:px-12 h-16 flex items-center gap-3 sm:gap-5">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
          <Tv className="text-brand" size={26} />
          <span className="font-head font-extrabold text-xl tracking-tight">
            NeTime<span className="text-brand">TV</span>
          </span>
        </Link>

        <div className="hidden 2xl:flex items-center gap-0.5 shrink-0">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={`px-2.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                loc.pathname === n.to ? "text-white bg-white/10" : "text-zinc-400 hover:text-white"
              }`}
            >
              {t(`nav.${n.key}`)}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin"
              className={`px-2.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                loc.pathname === "/admin" ? "text-gold bg-white/10" : "text-gold/80 hover:text-gold"}`}>
              <Shield size={14} /> {t("nav.research")}
            </Link>
          )}
        </div>

        <div className="flex-1" />

        <form onSubmit={submit} role="search" data-testid="search-form" className="hidden sm:flex items-center bg-white/10 rounded-full px-3 h-9 focus-within:ring-1 ring-brand shrink-0">
          <Search size={16} className="text-zinc-400" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("nav.searchPlaceholder")}
            type="search" enterKeyHint="search"
            className="bg-transparent outline-none text-sm px-2 w-36 min-[1800px]:w-56 placeholder:text-zinc-500"
          />
        </form>

        <Link to="/download" data-testid="get-app-btn"
          className="hidden min-[1800px]:flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium whitespace-nowrap bg-white/10 text-zinc-300 hover:bg-white/20 transition-colors">
          <Download size={15} /> {t("nav.getApp")}
        </Link>

        <button data-testid="kids-toggle" onClick={() => setKids(!kids)} title="Kids Mode"
          className={`hidden sm:flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-semibold transition-colors ${kids ? "bg-pink text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
          <Baby size={15} /> <span className="hidden min-[1800px]:inline">{t("nav.kids")}</span>
        </button>

        <div className="hidden sm:block"><SettingsMenu /></div>

        {user && !paid && !freeAccess && (
          <Link to="/subscribe" data-testid="go-premium-btn"
            className="hidden sm:flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-bold bg-gradient-to-r from-gold to-amber-500 text-black hover:brightness-110 transition">
            <Crown size={15} /> {t("nav.subscribe")}
          </Link>
        )}
        {paid && !freeAccess && user?.role !== "admin" && (
          <span className="hidden sm:flex items-center gap-1 text-xs font-bold text-gold px-2"><Crown size={13} /> {t("nav.subscribed")}</span>
        )}

        {user && (
          <button data-testid="invite-friends-btn" onClick={() => setInviteOpen(true)}
            className="hidden sm:flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-semibold bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-colors">
            <UserPlus size={15} /> <span className="hidden min-[1800px]:inline whitespace-nowrap">{t("invite.button")}</span>
          </button>
        )}

        {user ? (
          <div className="flex items-center gap-2">
            <InboxBell />
            <Link to="/profiles" data-testid="profile-avatar" title={activeProfile?.name || user.name}
              className="w-9 h-9 grid place-items-center rounded-lg bg-gradient-to-br from-brand to-red-700 text-sm font-extrabold hover:ring-2 ring-white transition">
              {activeProfile?.is_kids ? <Baby size={16} /> : ((activeProfile?.name || user.name || "U")[0]?.toUpperCase())}
            </Link>
            <button data-testid="logout-btn" onClick={logout} title={t("nav.logout")}
              className="w-9 h-9 grid place-items-center rounded-full bg-white/10 hover:bg-brand transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/login" data-testid="login-link" className="hidden sm:block text-sm text-zinc-300 hover:text-white px-3">{t("nav.signIn")}</Link>
            <Link to="/register" data-testid="register-link"
              className="text-sm font-semibold bg-brand hover:bg-brand-hover px-3 sm:px-4 py-1.5 rounded-full transition-colors flex items-center gap-1.5 whitespace-nowrap">
              <Crown size={14} /> {freeAccess ? t("free.join") : t("nav.subscribe")}
            </Link>
          </div>
        )}

        <button className="2xl:hidden" aria-label="Menu" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="2xl:hidden px-4 pb-4 flex flex-col gap-1 border-t border-white/10 pt-2">
          {!user && (
            <div className="flex gap-2 pb-2 mb-1 border-b border-white/10">
              <Link to="/login" onClick={() => setOpen(false)} data-testid="menu-login"
                className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-semibold bg-white/10 text-white">{t("nav.signIn")}</Link>
              <Link to="/register" onClick={() => setOpen(false)} data-testid="menu-register"
                className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-bold bg-brand text-white flex items-center justify-center gap-1.5"><Crown size={14} /> {freeAccess ? t("free.join") : t("nav.subscribe")}</Link>
            </div>
          )}
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg text-sm text-zinc-300 hover:bg-white/10">{t(`nav.${n.key}`)}</Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-gold">{t("nav.research")}</Link>
          )}
          <button onClick={() => setKids(!kids)} className={`text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${kids ? "text-pink" : "text-zinc-300"}`}>
            <Baby size={15} /> {t("nav.kids")} {kids ? "✓" : ""}
          </button>
          <div className="px-1 py-1"><SettingsMenu /></div>
          {user && (
            <button data-testid="menu-invite-friends" onClick={() => { setOpen(false); setInviteOpen(true); }}
              className="text-left px-3 py-2 rounded-lg text-sm text-emerald-300 flex items-center gap-2"><UserPlus size={15} /> {t("invite.button")}</button>
          )}
          {user && !paid && !freeAccess && (
            <Link to="/subscribe" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-gold flex items-center gap-2"><Crown size={15} /> {t("nav.subscribe")}</Link>
          )}
          <form onSubmit={submit} className="flex items-center bg-white/10 rounded-full px-3 h-9 mt-2">
            <Search size={16} className="text-zinc-400" />
            <input data-testid="mobile-search-input" type="search" enterKeyHint="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.searchPlaceholder")}
              className="bg-transparent outline-none text-sm px-2 flex-1" />
          </form>
        </div>
      )}
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </nav>
  );
};
